import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBookOpen,
  FaChartBar,
  FaClipboardCheck,
  FaFileInvoiceDollar,
  FaIndustry,
  FaSchool,
  FaTruck,
  FaUserGraduate,
} from 'react-icons/fa';
import { executiveAPI } from '../../utils/api';
import LoadingSpinner from '../../components_shared/LoadingSpinner';
import { DashboardChartGrid, KpiGrid, PortalHero, StatusRows } from '../../components_shared/PortalDashboard';

const empty = {
  enrollment: {},
  procurement: { status: { tenders: {}, workOrders: {} } },
  logistics: { status: {} },
  qc: {},
  distribution: { status: { requisitions: {}, handovers: {} } },
  finance: {},
};

const nf = new Intl.NumberFormat();

export default function MinisterDashboardPage() {
  const [summary, setSummary] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    executiveAPI.ministerSummary()
      .then(res => setSummary({ ...empty, ...(res.data || {}) }))
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, []);

  const cards = useMemo(() => [
    { icon: <FaSchool />, label: 'Schools', value: summary.enrollment.schools || 0, desc: `${nf.format(summary.enrollment.collectedSchools || 0)} reporting enrollment`, className: 'eduss-dashboard-card-blue' },
    { icon: <FaUserGraduate />, label: 'Enrollment', value: nf.format(summary.enrollment.totalStudents || 0), desc: 'Students captured province-wide', className: 'eduss-dashboard-card-green' },
    { icon: <FaBookOpen />, label: 'Tenders', value: summary.procurement.tenders || 0, desc: `${nf.format(summary.procurement.assignedTenders || 0)} assigned to publishers`, className: 'eduss-dashboard-card-yellow' },
    { icon: <FaIndustry />, label: 'Work Orders', value: summary.procurement.workOrders || 0, desc: `${nf.format(summary.procurement.completedWorkOrders || 0)} completed`, className: 'eduss-dashboard-card-green' },
    { icon: <FaTruck />, label: 'Deliveries', value: summary.logistics.dispatches || 0, desc: `${nf.format(summary.logistics.receivedFinal || 0)} final received`, className: 'eduss-dashboard-card-blue' },
    { icon: <FaClipboardCheck />, label: 'Handovers', value: summary.distribution.handovers || 0, desc: `${nf.format(summary.distribution.acknowledgedHandovers || 0)} acknowledged`, className: 'eduss-dashboard-card-green' },
    { icon: <FaChartBar />, label: 'QC Passed', value: summary.qc.passed || 0, desc: `${nf.format(summary.qc.failed || 0)} failed/rejected`, className: 'eduss-dashboard-card-yellow' },
    { icon: <FaFileInvoiceDollar />, label: 'Finance', value: nf.format(summary.finance.invoices || 0), desc: `Rs. ${nf.format(summary.finance.invoiceAmount || 0)} invoiced`, className: 'eduss-dashboard-card-blue' },
  ], [summary]);

  const charts = useMemo(() => [
    {
      title: 'Province Overview',
      caption: 'Key volumes',
      type: 'bar',
      data: [
        { name: 'Schools', value: Number(summary.enrollment.schools || 0) },
        { name: 'Students', value: Number(summary.enrollment.totalStudents || 0) },
        { name: 'Tenders', value: Number(summary.procurement.tenders || 0) },
        { name: 'Orders', value: Number(summary.procurement.workOrders || 0) },
      ],
      color: '#0f6fad',
    },
    {
      title: 'Operational Flow',
      caption: 'Lifecycle health',
      type: 'line',
      data: [
        { name: 'Deliveries', value: Number(summary.logistics.dispatches || 0) },
        { name: 'Received', value: Number(summary.logistics.receivedFinal || 0) },
        { name: 'QC Passed', value: Number(summary.qc.passed || 0) },
        { name: 'Handovers', value: Number(summary.distribution.handovers || 0) },
      ],
      color: '#10ac84',
    },
  ], [summary]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="eduss-error-alert">{error}</div>;

  return (
    <div className="eduss-dashboard">
      <PortalHero title="Minister Executive Dashboard" subtitle="Province-wide read-only view of enrollment, procurement, logistics, QC, distribution, and finance." />
      <KpiGrid cards={cards} />
      <DashboardChartGrid charts={charts} />

      <div className="eduss-dashboard-content">
        <h2>Operational Status</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
          <div>
            <h3>Tenders</h3>
            <StatusRows rows={summary.procurement.status?.tenders} />
          </div>
          <div>
            <h3>Work Orders</h3>
            <StatusRows rows={summary.procurement.status?.workOrders} />
          </div>
          <div>
            <h3>Deliveries</h3>
            <StatusRows rows={summary.logistics.status} />
          </div>
          <div>
            <h3>Distribution</h3>
            <StatusRows rows={{
              ...(summary.distribution.status?.requisitions || {}),
              ...(summary.distribution.status?.handovers || {}),
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}
